/**
 * SolicitudesView — Sprint 17 S17-12 tab Solicitudes.
 *
 * 3 sub-listas operativas para el tesorero:
 * - Esperando cobro: solicitudes aprobadas con IV en `por_cobrar`.
 * - Asignadas recientes: estado=asignada, últimos 30 días.
 * - Histórico: terminadas (rechazada / cancelada_*).
 *
 * Recibe la lista enriquecida ya particionada en 3 por el wrapper. La
 * aprobación/rechazo viven en /admin/validaciones (comisión). La única
 * acción del tesorero aquí es **anular el cobro** de una solicitud en
 * esperando_cobro (C5 / D-S17-5): cuando el vecino no abona, se anula el
 * cargo y la solicitud queda cancelada. Se habilita solo si llega `onAnular`.
 */
import { useEffect, useState } from 'react';
import type {
  SolicitudRecursoConDetalle,
  StockRecursoRead,
  TipoStock,
} from '../../lib/api/types';
import { listStockRecursos } from '../../lib/api/stock-recursos';
import { formatFecha } from '../../lib/format';
import { RECURSO_TIPO_LABEL } from '../../lib/labels';
import { Monto, Num } from './Monto';
import { Button } from '../ui/Button';

interface Props {
  esperandoCobro: SolicitudRecursoConDetalle[];
  /** 2026-06: solicitudes pagadas/validadas listas para entrega física. */
  porEntregar?: SolicitudRecursoConDetalle[];
  asignadasRecientes: SolicitudRecursoConDetalle[];
  historico: SolicitudRecursoConDetalle[];
  loading: boolean;
  error: string | null;
  /** C5: anula el cobro de una solicitud en esperando_cobro. Devuelve
   * mensaje de error o null si fue OK. Si se omite, no se muestra la acción. */
  onAnular?: (id: string, motivo: string) => Promise<string | null>;
  /** 2026-06: cobra una solicitud en esperando_cobro con el saldo a favor
   * de la casa (sin pago nuevo). Devuelve mensaje de error o null si fue OK.
   * Si se omite, no se muestra la acción. */
  onPagarSaldo?: (id: string) => Promise<string | null>;
  /** 2026-06: marca entregado (entrega física) una solicitud en
   * esperando_retiro. Devuelve mensaje de error o null si fue OK. */
  onEntregar?: (id: string, stockId: string) => Promise<string | null>;
  /** RP-05: cancela una entrega en esperando_retiro. Si fue pagada,
   * `destino` decide el destino del dinero (saldo a favor / reembolso).
   * Devuelve mensaje de error o null si fue OK. */
  onCancelarEntrega?: (
    id: string,
    motivo: string,
    destino: 'saldo_a_favor' | 'reembolso',
  ) => Promise<string | null>;
}

const ETIQUETA_MOTIVO: Record<string, string> = {
  perdida: 'Pérdida',
  robo: 'Robo',
  deterioro: 'Deterioro',
  falla_fabrica: 'Falla de fábrica',
  nuevo_residente: 'Nuevo residente',
  nuevo_vehiculo: 'Nuevo vehículo',
  otro: 'Otro',
};

const ETIQUETA_ESTADO: Record<string, string> = {
  // Sprint 18 P11: vocabulario validación. Enums internos sin cambiar.
  pendiente_aprobacion: 'En revisión',
  aprobada: 'Validada',
  esperando_cobro: 'Esperando cobro',
  esperando_retiro: 'Por entregar',
  esperando_asignacion: 'Esperando asignación',
  asignada: 'Asignada',
  rechazada: 'No validada',
  cancelada_por_cascada: 'Cancelada (cascada)',
  cancelada_manual: 'Cancelada',
};

const COLOR_ESTADO: Record<string, string> = {
  esperando_cobro: 'text-state-warning',
  esperando_retiro: 'text-state-success',
  esperando_asignacion: 'text-state-info',
  asignada: 'text-state-success',
  rechazada: 'text-state-danger',
  cancelada_por_cascada: 'text-text-muted',
  cancelada_manual: 'text-text-muted',
};

function FilaSolicitud({
  s,
  mostrarMonto,
  onPedirAnular,
  onPedirPagarSaldo,
  onPedirEntregar,
  onPedirCancelarEntrega,
}: {
  s: SolicitudRecursoConDetalle;
  mostrarMonto: boolean;
  onPedirAnular?: (s: SolicitudRecursoConDetalle) => void;
  onPedirPagarSaldo?: (s: SolicitudRecursoConDetalle) => void;
  onPedirEntregar?: (s: SolicitudRecursoConDetalle) => void;
  onPedirCancelarEntrega?: (s: SolicitudRecursoConDetalle) => void;
}) {
  return (
    <tr
      data-testid="fila-solicitud"
      className="border-b border-white/[0.04] hover:bg-bg-card/40"
    >
      <td className="px-3 py-2 text-text-muted">{formatFecha(s.solicitado_at.slice(0, 10))}</td>
      <td className="px-3 py-2 text-text-primary">
        Casa <Num>{s.lote_identificador}</Num>
      </td>
      <td className="px-3 py-2 text-text-primary">{s.vecino_nombre}</td>
      <td className="px-3 py-2 text-text-secondary">
        {RECURSO_TIPO_LABEL[s.tipo]}
      </td>
      <td className="px-3 py-2 text-text-muted text-xs">
        {ETIQUETA_MOTIVO[s.motivo] ?? s.motivo}
      </td>
      <td className={`px-3 py-2 font-medium ${COLOR_ESTADO[s.estado] ?? 'text-text-secondary'}`}>
        {ETIQUETA_ESTADO[s.estado] ?? s.estado}
      </td>
      {mostrarMonto && (
        <td className="px-3 py-2 text-right text-text-primary">
          {s.monto_a_cobrar !== null ? <Monto value={s.monto_a_cobrar} /> : '—'}
        </td>
      )}
      {(onPedirAnular ||
        onPedirPagarSaldo ||
        onPedirEntregar ||
        onPedirCancelarEntrega) && (
        <td className="px-3 py-2 text-right space-x-3">
          {onPedirPagarSaldo && (
            <button
              type="button"
              onClick={() => onPedirPagarSaldo(s)}
              className="text-xs text-state-success hover:underline"
            >
              Pagar con saldo a favor
            </button>
          )}
          {onPedirAnular && (
            <button
              type="button"
              onClick={() => onPedirAnular(s)}
              className="text-xs text-state-danger hover:underline"
            >
              Anular cobro
            </button>
          )}
          {onPedirEntregar && (
            <button
              type="button"
              onClick={() => onPedirEntregar(s)}
              className="text-xs text-state-success hover:underline"
            >
              Marcar entregado
            </button>
          )}
          {onPedirCancelarEntrega && (
            <button
              type="button"
              onClick={() => onPedirCancelarEntrega(s)}
              className="text-xs text-state-danger hover:underline"
            >
              Cancelar entrega
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

function TablaSeccion({
  solicitudes,
  vacioMensaje,
  mostrarMonto,
  onPedirAnular,
  onPedirPagarSaldo,
  onPedirEntregar,
  onPedirCancelarEntrega,
}: {
  solicitudes: SolicitudRecursoConDetalle[];
  vacioMensaje: string;
  mostrarMonto: boolean;
  onPedirAnular?: (s: SolicitudRecursoConDetalle) => void;
  onPedirPagarSaldo?: (s: SolicitudRecursoConDetalle) => void;
  onPedirEntregar?: (s: SolicitudRecursoConDetalle) => void;
  onPedirCancelarEntrega?: (s: SolicitudRecursoConDetalle) => void;
}) {
  if (solicitudes.length === 0) {
    return <p className="text-xs text-text-muted">{vacioMensaje}</p>;
  }
  return (
    <div className="overflow-x-auto bg-bg-card border-[0.5px] border-white/[0.08] rounded-md">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Solicitado</th>
            <th className="text-left px-3 py-2 font-medium">Casa</th>
            <th className="text-left px-3 py-2 font-medium">Vecino</th>
            <th className="text-left px-3 py-2 font-medium">Tipo</th>
            <th className="text-left px-3 py-2 font-medium">Motivo</th>
            <th className="text-left px-3 py-2 font-medium">Estado</th>
            {mostrarMonto && (
              <th className="text-right px-3 py-2 font-medium">A cobrar</th>
            )}
            {(onPedirAnular ||
              onPedirPagarSaldo ||
              onPedirEntregar ||
              onPedirCancelarEntrega) && (
              <th className="text-right px-3 py-2 font-medium">Acción</th>
            )}
          </tr>
        </thead>
        <tbody>
          {solicitudes.map((s) => (
            <FilaSolicitud
              key={s.id}
              s={s}
              mostrarMonto={mostrarMonto}
              onPedirAnular={onPedirAnular}
              onPedirPagarSaldo={onPedirPagarSaldo}
              onPedirEntregar={onPedirEntregar}
              onPedirCancelarEntrega={onPedirCancelarEntrega}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModalAnular({
  solicitud,
  onConfirmar,
  onCerrar,
}: {
  solicitud: SolicitudRecursoConDetalle;
  onConfirmar: (motivo: string) => Promise<string | null>;
  onCerrar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valido = motivo.trim().length > 0;

  async function handleConfirmar() {
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    const err = await onConfirmar(motivo.trim());
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">Anular cobro</h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          Vas a anular el cobro de{' '}
          <strong className="text-text-primary">
            Casa <Num>{solicitud.lote_identificador}</Num>
          </strong>{' '}
          ({RECURSO_TIPO_LABEL[solicitud.tipo]}
          {solicitud.monto_a_cobrar !== null && (
            <>
              , <Monto value={solicitud.monto_a_cobrar} />
            </>
          )}
          ). Se anulará el cargo asociado y la solicitud quedará{' '}
          <strong>cancelada</strong>. La acción queda registrada.
        </p>
        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Motivo <span className="text-state-danger">*</span>
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            disabled={submitting}
            autoFocus
            placeholder="Ej: el vecino desistió / no abonó dentro del plazo"
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
          />
        </label>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={!valido || submitting}
          >
            {submitting ? 'Anulando…' : 'Anular cobro'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModalEntregar({
  solicitud,
  onConfirmar,
  onCerrar,
}: {
  solicitud: SolicitudRecursoConDetalle;
  onConfirmar: (stockId: string) => Promise<string | null>;
  onCerrar: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El tesorero DEBE elegir cuál item físico entregó (ej. qué LL-NNNN tomó del
  // cajón): el número registrado tiene que coincidir con el que dio.
  const [stock, setStock] = useState<StockRecursoRead[]>([]);
  const [stockId, setStockId] = useState<string>('');
  const [cargandoStock, setCargandoStock] = useState(true);

  useEffect(() => {
    let cancelado = false;
    listStockRecursos({
      tipo: solicitud.tipo as TipoStock,
      estado: 'en_stock',
    })
      .then((items) => {
        if (!cancelado) setStock(items);
      })
      .catch(() => {
        if (!cancelado) setStock([]);
      })
      .finally(() => {
        if (!cancelado) setCargandoStock(false);
      });
    return () => {
      cancelado = true;
    };
  }, [solicitud.tipo]);

  async function handleConfirmar() {
    if (submitting) return;
    if (!stockId) {
      setError('Elige del stock el item que entregaste.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const err = await onConfirmar(stockId);
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Marcar entregado
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          Confirma la entrega física a{' '}
          <strong className="text-text-primary">
            Casa <Num>{solicitud.lote_identificador}</Num>
          </strong>{' '}
          ({RECURSO_TIPO_LABEL[solicitud.tipo]}). Elige del stock el item que
          entregaste; se creará el recurso con ese número y la solicitud quedará{' '}
          <strong>asignada</strong>.
        </p>

        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Item entregado <span className="text-state-danger">*</span>
          </span>
          <select
            value={stockId}
            onChange={(e) => setStockId(e.target.value)}
            disabled={cargandoStock || submitting}
            required
            className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-2 py-2 text-sm"
          >
            <option value="">
              {cargandoStock
                ? 'Cargando stock…'
                : stock.length === 0
                  ? '— Sin stock disponible —'
                  : '— Elige el item que entregaste —'}
            </option>
            {stock.map((s) => (
              <option key={s.id} value={s.id}>
                {s.identificador ?? '(sin código)'}
                {s.serial_rfid ? ` · serie ${s.serial_rfid}` : ''}
              </option>
            ))}
          </select>
          {!cargandoStock && stock.length === 0 && (
            <span className="text-xs text-state-warning block">
              No hay items en stock de este tipo. Ingresa stock antes de
              entregar.
            </span>
          )}
        </label>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={submitting || cargandoStock || !stockId}
          >
            {submitting ? 'Entregando…' : 'Marcar entregado'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModalCancelarEntrega({
  solicitud,
  onConfirmar,
  onCerrar,
}: {
  solicitud: SolicitudRecursoConDetalle;
  onConfirmar: (
    motivo: string,
    destino: 'saldo_a_favor' | 'reembolso',
  ) => Promise<string | null>;
  onCerrar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [destino, setDestino] = useState<'saldo_a_favor' | 'reembolso'>(
    'saldo_a_favor',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El destino del dinero solo aplica si la entrega fue pagada.
  const fuePagada = solicitud.iv_estado === 'cobrado';
  const valido = motivo.trim().length > 0;

  async function handleConfirmar() {
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    const err = await onConfirmar(motivo.trim(), destino);
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Cancelar entrega
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          Cancela la entrega de{' '}
          <strong className="text-text-primary">
            Casa <Num>{solicitud.lote_identificador}</Num>
          </strong>{' '}
          ({RECURSO_TIPO_LABEL[solicitud.tipo]}). La solicitud quedará
          cancelada.
        </p>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Motivo</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            placeholder="Ej: el vecino se fue del barrio"
            required
          />
        </label>

        {fuePagada ? (
          <fieldset className="space-y-2">
            <legend className="text-text-secondary text-sm mb-1">
              Esta entrega fue pagada. ¿Qué hacemos con el dinero?
            </legend>
            <label className="flex items-start gap-2 text-sm text-text-primary">
              <input
                type="radio"
                name="destino-dinero"
                checked={destino === 'saldo_a_favor'}
                onChange={() => setDestino('saldo_a_favor')}
                className="mt-1"
              />
              <span>
                <strong>Saldo a favor</strong> de la casa — queda como crédito
                para futuros cobros/cuotas.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-text-primary">
              <input
                type="radio"
                name="destino-dinero"
                checked={destino === 'reembolso'}
                onChange={() => setDestino('reembolso')}
                className="mt-1"
              />
              <span>
                <strong>Reembolso</strong> — le transfieres de vuelta y
                registras el gasto de devolución en la pantalla de Gastos.
              </span>
            </label>
          </fieldset>
        ) : (
          <p className="text-xs text-text-muted border-l-2 border-text-muted/30 pl-3">
            Esta entrega no tuvo costo; no hay dinero que devolver.
          </p>
        )}

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={!valido || submitting}
          >
            {submitting ? 'Cancelando…' : 'Cancelar entrega'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModalPagarSaldo({
  solicitud,
  onConfirmar,
  onCerrar,
}: {
  solicitud: SolicitudRecursoConDetalle;
  onConfirmar: () => Promise<string | null>;
  onCerrar: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirmar() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const err = await onConfirmar();
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Pagar con saldo a favor
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            disabled={submitting}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          Vas a cubrir el cobro de{' '}
          <strong className="text-text-primary">
            Casa <Num>{solicitud.lote_identificador}</Num>
          </strong>{' '}
          ({RECURSO_TIPO_LABEL[solicitud.tipo]}
          {solicitud.monto_a_cobrar !== null && (
            <>
              , <Monto value={solicitud.monto_a_cobrar} />
            </>
          )}
          ) usando el <strong>saldo a favor</strong> de la casa, sin un pago
          nuevo. La solicitud quedará <strong>por entregar</strong>. Si el
          saldo a favor no alcanza, la operación se rechaza.
        </p>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleConfirmar}
            disabled={submitting}
          >
            {submitting ? 'Aplicando…' : 'Pagar con saldo a favor'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SolicitudesView({
  esperandoCobro,
  porEntregar = [],
  asignadasRecientes,
  historico,
  loading,
  error,
  onAnular,
  onPagarSaldo,
  onEntregar,
  onCancelarEntrega,
}: Props) {
  const [anulando, setAnulando] = useState<SolicitudRecursoConDetalle | null>(
    null,
  );
  const [pagandoSaldo, setPagandoSaldo] =
    useState<SolicitudRecursoConDetalle | null>(null);
  const [entregando, setEntregando] =
    useState<SolicitudRecursoConDetalle | null>(null);
  const [cancelandoEntrega, setCancelandoEntrega] =
    useState<SolicitudRecursoConDetalle | null>(null);
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-sm text-text-muted max-w-3xl">
          Solicitudes de recurso (llaveros y controles remotos) en sus
          distintos estados. La aprobación y el rechazo se gestionan
          desde la cola de la <strong>comisión</strong>. Aquí ves el
          contexto operativo: qué hay esperando cobro, qué se asignó
          recientemente y el histórico. Si un vecino no abona, puedes{' '}
          <strong>anular el cobro</strong> de una solicitud en espera.
        </p>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <section className="space-y-2" data-testid="seccion-esperando-cobro">
            <h3 className="text-xs uppercase tracking-wide text-text-secondary font-medium">
              Esperando cobro ({esperandoCobro.length})
            </h3>
            <TablaSeccion
              solicitudes={esperandoCobro}
              vacioMensaje="No hay solicitudes esperando cobro."
              mostrarMonto={true}
              onPedirAnular={onAnular ? (s) => setAnulando(s) : undefined}
              onPedirPagarSaldo={
                onPagarSaldo ? (s) => setPagandoSaldo(s) : undefined
              }
            />
          </section>

          <section className="space-y-2" data-testid="seccion-por-entregar">
            <h3 className="text-xs uppercase tracking-wide text-text-secondary font-medium">
              Por entregar ({porEntregar.length})
            </h3>
            <TablaSeccion
              solicitudes={porEntregar}
              vacioMensaje="No hay recursos listos para entregar."
              mostrarMonto={false}
              onPedirEntregar={
                onEntregar ? (s) => setEntregando(s) : undefined
              }
              onPedirCancelarEntrega={
                onCancelarEntrega ? (s) => setCancelandoEntrega(s) : undefined
              }
            />
          </section>

          <section
            className="space-y-2"
            data-testid="seccion-asignadas-recientes"
          >
            <h3 className="text-xs uppercase tracking-wide text-text-secondary font-medium">
              Asignadas recientes ({asignadasRecientes.length})
            </h3>
            <TablaSeccion
              solicitudes={asignadasRecientes}
              vacioMensaje="Sin asignaciones recientes (últimos 30 días)."
              mostrarMonto={false}
            />
          </section>

          <section className="space-y-2" data-testid="seccion-historico">
            <h3 className="text-xs uppercase tracking-wide text-text-secondary font-medium">
              Histórico ({historico.length})
            </h3>
            <TablaSeccion
              solicitudes={historico}
              vacioMensaje="Sin solicitudes terminadas."
              mostrarMonto={false}
            />
          </section>
        </>
      )}

      {anulando && onAnular && (
        <ModalAnular
          solicitud={anulando}
          onConfirmar={(motivo) => onAnular(anulando.id, motivo)}
          onCerrar={() => setAnulando(null)}
        />
      )}

      {pagandoSaldo && onPagarSaldo && (
        <ModalPagarSaldo
          solicitud={pagandoSaldo}
          onConfirmar={() => onPagarSaldo(pagandoSaldo.id)}
          onCerrar={() => setPagandoSaldo(null)}
        />
      )}

      {entregando && onEntregar && (
        <ModalEntregar
          solicitud={entregando}
          onConfirmar={(stockId) => onEntregar(entregando.id, stockId)}
          onCerrar={() => setEntregando(null)}
        />
      )}

      {cancelandoEntrega && onCancelarEntrega && (
        <ModalCancelarEntrega
          solicitud={cancelandoEntrega}
          onConfirmar={(motivo, destino) =>
            onCancelarEntrega(cancelandoEntrega.id, motivo, destino)
          }
          onCerrar={() => setCancelandoEntrega(null)}
        />
      )}
    </div>
  );
}
